// The top search's legs (messages, tasks, pages, skills) and their sections, shared by the desktop
// bar (MessageSearch) and the mobile overlay. Each leg settles on its own and fails only its
// own section; one race guard drops a superseded response.
import type { Message, PageSearchResult, TaskSearchResult } from "$lib/core/types.js";
import type { SkillSearchHit } from "$lib/components/panes/skills-library-view-model.js";
import {
  SEARCH_SECTION_PREVIEW,
  orderSections,
  reportSearchLegFailure,
  sectionFreshness,
  settledLeg,
  type SearchSection,
} from "./global-search.js";

// A leg left out is not searched and is not a section (the Docs filter runs pages only).
export interface SearchLegs {
  workspaceId: string;
  messages?: Promise<Message[]>;
  tasks?: Promise<TaskSearchResult[]>;
  pages?: Promise<PageSearchResult[]>;
  skills?: Promise<SkillSearchHit[]>;
}

const ALL_SECTIONS: readonly SearchSection[] = ["messages", "tasks", "pages", "skills"];
const SKIPPED: Promise<never[]> = Promise.resolve([]);

export interface SearchRunOptions<A> {
  // A messages rejection that is a fixed answer for the workspace, not a failure.
  isRefusal?: (err: unknown) => boolean;
  // Awaited with the legs and committed under the same race guard (the desktop's channel list).
  alongside?: Promise<A>;
}

export type SearchRunOutcome<A> =
  | { current: false }
  | { current: true; refused: boolean; alongside: A | undefined };

export class GlobalSearchState {
  results = $state<Message[]>([]);
  taskResults = $state<TaskSearchResult[]>([]);
  pageResults = $state<PageSearchResult[]>([]);
  skillResults = $state<SkillSearchHit[]>([]);
  messagesFailed = $state(false);
  tasksFailed = $state(false);
  pagesFailed = $state(false);
  skillsFailed = $state(false);
  loading = $state(false);
  messagesExpanded = $state(false);
  tasksExpanded = $state(false);
  pagesExpanded = $state(false);
  skillsExpanded = $state(false);
  // The sections the last run searched; the others neither render nor take keyboard rows.
  sections = $state<readonly SearchSection[]>(ALL_SECTIONS);

  readonly visibleMessages = $derived(
    this.messagesExpanded ? this.results : this.results.slice(0, SEARCH_SECTION_PREVIEW),
  );
  readonly visibleTasks = $derived(
    this.tasksExpanded ? this.taskResults : this.taskResults.slice(0, SEARCH_SECTION_PREVIEW),
  );
  readonly visiblePages = $derived(
    this.pagesExpanded ? this.pageResults : this.pageResults.slice(0, SEARCH_SECTION_PREVIEW),
  );
  readonly visibleSkills = $derived(
    this.skillsExpanded ? this.skillResults : this.skillResults.slice(0, SEARCH_SECTION_PREVIEW),
  );
  // Freshest hit first; a loading or failed section has no timestamp and goes last. Skills
  // always comes after them.
  readonly sectionOrder = $derived(
    this.filterSections([
      ...orderSections(
        sectionFreshness({
          messages: this.loading || this.messagesFailed ? null : this.results, // null: no timestamp (sectionFreshness)
          tasks: this.tasksFailed ? null : this.taskResults, // null: no timestamp (sectionFreshness)
          pages: this.pagesFailed ? null : this.pageResults, // null: no timestamp (sectionFreshness)
        }),
      ),
      "skills",
    ]),
  );
  // Nothing found and nothing failed: the one state that may say "No results".
  readonly foundNothing = $derived(
    this.results.length === 0 &&
      this.taskResults.length === 0 &&
      this.pageResults.length === 0 &&
      this.skillResults.length === 0 &&
      !this.messagesFailed &&
      !this.tasksFailed &&
      !this.pagesFailed &&
      !this.skillsFailed,
  );

  private seq = 0;

  private filterSections(order: SearchSection[]): SearchSection[] {
    return order.filter((section) => this.sections.includes(section));
  }

  private clearFailures(): void {
    this.messagesFailed = false;
    this.tasksFailed = false;
    this.pagesFailed = false;
    this.skillsFailed = false;
  }

  // A newer keystroke supersedes the request in flight (bumped when it is scheduled, not only
  // when it runs, or that request's failure renders against a query nobody has searched yet).
  supersede(): void {
    this.seq++;
    this.clearFailures();
  }

  clear(): void {
    this.results = [];
    this.taskResults = [];
    this.pageResults = [];
    this.skillResults = [];
    this.clearFailures();
    this.messagesExpanded = false;
    this.tasksExpanded = false;
    this.pagesExpanded = false;
    this.skillsExpanded = false;
    this.sections = ALL_SECTIONS;
    this.loading = false;
  }

  async run<A = never>(
    legs: SearchLegs,
    opts: SearchRunOptions<A> = {},
  ): Promise<SearchRunOutcome<A>> {
    const mine = ++this.seq;
    this.loading = true;
    this.clearFailures();
    const sections = ALL_SECTIONS.filter((section) => legs[section] !== undefined);
    const [msgLeg, taskLeg, pageLeg, skillLeg] = await Promise.allSettled([
      legs.messages ?? SKIPPED,
      legs.tasks ?? SKIPPED,
      legs.pages ?? SKIPPED,
      legs.skills ?? SKIPPED,
    ]);
    let refused = false;
    let messagesFailed = false;
    if (msgLeg.status === "rejected") {
      if (opts.isRefusal?.(msgLeg.reason)) refused = true;
      else {
        reportSearchLegFailure("messages", msgLeg.reason, legs.workspaceId);
        messagesFailed = true;
      }
    }
    const tasks = settledLeg(taskLeg, "tasks", legs.workspaceId);
    const pages = settledLeg(pageLeg, "pages", legs.workspaceId);
    const skills = settledLeg(skillLeg, "skills", legs.workspaceId);
    const alongside = await opts.alongside;
    if (mine !== this.seq) return { current: false };
    this.results = msgLeg.status === "fulfilled" ? msgLeg.value : [];
    this.taskResults = tasks.value;
    this.pageResults = pages.value;
    this.skillResults = skills.value;
    this.messagesFailed = messagesFailed;
    this.tasksFailed = tasks.failed;
    this.pagesFailed = pages.failed;
    this.skillsFailed = skills.failed;
    this.messagesExpanded = false;
    this.tasksExpanded = false;
    this.pagesExpanded = false;
    this.skillsExpanded = false;
    this.sections = sections;
    this.loading = false;
    return { current: true, refused, alongside };
  }
}
